<script setup lang="ts">
import { Heart } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Product } from '@/types/products';

import BrittanyIcon from '@/components/labels/BrittanyIcon.vue';
import EcoFriendlyIcon from '@/components/labels/EcoFriendlyIcon.vue';
import { useFavorites } from '@/composables/useFavorites.js';
import { useProductLink } from '@/composables/useProductLink';
import { vGtmTrack } from '@/directives/v-gtm-track.js';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import {
  convertUrlToResizer,
  formatActivityDuration,
  formatDisplayRating,
  isEcoFriendly,
  isProducedInBrittany,
} from '@/utils/utils.js';

const props = withDefaults(
  defineProps<{
    product: Product;
    showFavorite?: boolean;
    prefixUrl: string | null;
  }>(),
  {
    showFavorite: true,
  },
);

const { handleAddCrossSellingToFavorite } = useFavorites();
const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration, currentLocale, componentsBaseUrl } = storeToRefs(globalConfigurationStore);

const { t } = useI18n();
const { properties: _, ...rest } = props.product;

const isFavorite = ref<boolean>(false);
const imageSrc = ref<string>(props.product.image ?? props.product.images?.[0]?.url ?? 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png');

const { productLink } = useProductLink(() => props.product, () => props.prefixUrl);
const externalLink = computed(() => productLink.value ?? '');

const isDiffusionMode = computed(() => !!globalConfiguration.value?.diffusion);

const duration = computed(() => props.product.properties.find(it => it.propertyName === 'nextSessionDate')?.value?.duration ?? 0);
const computedDuration = computed(() => {
  const numberOfDays = props.product.properties.find(it => it.propertyName === 'nextSessionDate')?.value?.numberOfDays ?? 0;
  if (numberOfDays > 1) {
    return `${numberOfDays} jours`;
  }
  else {
    return formatActivityDuration(duration.value);
  }
});

const rating = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.rating ?? null);
const displayedRating = ref(formatDisplayRating(rating.value ?? 0));

const ratingReviewCount = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.ratingReviewCount ?? null);
const ratingSource = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.source ?? null);

const locality = `${props.product.location?.locality}`;

const hasMarketingTag = computed(() => 'marketingTag' in props.product);
const marketingTagLabel = computed(() => props.product.marketingTag?.name.find(it => it.lang === currentLocale.value)?.value);

const cardAriaLabel = computed(() => `${props.product.name ?? ''}${locality ? `, ${locality}` : ''}`);

function handleImgError() {
  imageSrc.value = 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png';
}

function handleFavorite(event: Event) {
  event.preventDefault();
  event.stopPropagation();
  handleAddCrossSellingToFavorite(event, isFavorite);
}

function handleCardClick(event: Event) {
  if (!isDiffusionMode.value)
    return;
  event.preventDefault();
  const url = new URL(window.location.href);
  url.searchParams.set('product', props.product.id);
  window.open(url.toString(), '_blank');
}
</script>

<template>
  <article v-gtm-track:card="{ product: rest }" class="carrousel-card" :aria-label="cardAriaLabel">
    <div class="carrousel-card_media">
      <div v-if="isEcoFriendly(product)" class="eco-friendly-badge">
        <EcoFriendlyIcon />
      </div>
      <div v-if="isProducedInBrittany(product)" class="brittany-badge">
        <BrittanyIcon />
      </div>
      <div
        v-if="hasMarketingTag && marketingTagLabel" class="marketing-tag"
        :style="{ '--tagText': product.marketingTag?.textColor, '--tagColor': product.marketingTag?.backgroundColor, 'left': (isEcoFriendly(product) || isProducedInBrittany(product)) ? '3rem' : '0.75rem' }"
      >
        {{ marketingTagLabel }}
      </div>
      <img
        :src="convertUrlToResizer(imageSrc)"
        :alt="product.name ?? ''"
        loading="lazy"
        decoding="async"
        width="270"
        height="203"
        @error="handleImgError"
      >
      <button
        v-if="showFavorite" type="button" :data-id="product.id" class="favorite"
        :aria-label="t('card.favorite')" :aria-pressed="isFavorite" @click="handleFavorite" @keydown.enter="handleFavorite"
      >
        <Heart :class="{ checked: isFavorite }" aria-hidden="true" />
      </button>
    </div>
    <div class="carrousel-card_infos">
      <p class="small_text">
        {{ locality }}
      </p>
      <h3 class="title_text_clamp">
        <a class="card-link" :href="externalLink" target="_blank" rel="noopener noreferrer" @click="handleCardClick">
          {{ product.name ?? 'No name found.' }}
        </a>
      </h3>
      <p v-if="duration" class="small_text">
        {{ computedDuration }}
      </p>
      <div v-if="rating" class="rating">
        <svg class="external-star" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
        <p class="small_text">
          {{ displayedRating }}
        </p>
        <span v-if="ratingReviewCount" class="review-count">({{ ratingReviewCount }})</span>
        <img v-if="ratingSource === 'GOOGLE'" class="provider-logo" :src="`${componentsBaseUrl}/logo_google.jpg`" alt="" loading="lazy" decoding="async">
      </div>
      <p class="price_tag">
        {{ t('card.from') }}
        <span
          :style="[product.displayedPrice.hasDiscount ? 'text-decoration: line-through; font-weight: var(--yb-text-light); color:var(--yb-color-black)' : 'color:var(--yb-color-black);']"
        >
          {{ product.displayedPrice.originalPrice }}
        </span>&nbsp;
        <span
          v-if="product.displayedPrice.hasDiscount"
          style="color: var(--yb-color-primary)"
        >{{ product.displayedPrice.priceFrom }}</span>
      </p>
    </div>
  </article>
</template>

<style lang="css" scoped>
.carrousel-card {
  --img-ratio: 4/3;

  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-template-rows: auto;
  grid-auto-flow: row;
  gap: var(--yb-spacing-sm);
  width: 100%;
  /* ~13% shorter than the standard row-card height, keeps layout/aspect ratios intact. */
  min-height: calc(var(--yb-small-card-height) * 0.87);
  box-sizing: border-box;
  background-color: var(--yb-color-white);
  border: 1px solid #ccc;
  border-radius: var(--yb-card-radius);
  overflow: hidden;
  position: relative;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  &:focus-within {
    outline: 2px solid var(--yb-outline);
    outline-offset: 2px;
  }
}

.carrousel-card_media {
  container: card / inline-size;
  position: relative;
  width: 100%;
  height: 100%;
}

.carrousel-card_media img {
  width: 100%;
  height: 100%;
  aspect-ratio: var(--img-ratio);
  object-fit: cover;
  object-position: center;
}

.marketing-tag {
  position: absolute;
  z-index: 3;
  top: 0.75rem;
  background-color: var(--tagColor);
  color: var(--tagText);
  border-radius: 1rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: bold;

  @container card (width < 200px) {
    display: none;
  }
}

.favorite {
  all: unset;
  position: absolute;
  display: flex;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 5;
  cursor: pointer;
  transition: scale 0.3s ease-out;

  &:focus-visible {
    outline: 2px solid var(--yb-outline);
  }

  svg {
    stroke: var(--yb-color-white);
    stroke-width: 2px;
    fill: rgba(0, 0, 0, 0.4);
  }

  svg.checked {
    stroke: var(--yb-color-primary);
    stroke-width: 2px;
    fill: var(--yb-color-primary);
  }

  @media (prefers-reduced-motion: no-preference) {
    &:hover {
      scale: 1.1;
    }
  }
}

.brittany-badge,
.eco-friendly-badge {
  position: absolute;
  display: flex;
  background-color: var(--yb-color-ecofriendly);
  padding: 0.5rem;
  top: 0;
  left: 0.5rem;
  border-radius: 0 0 var(--yb-radius-sm) var(--yb-radius-sm);
  z-index: 3;
}

.brittany-badge {
  background-color: transparent;
  padding: 0.125rem;
  top: 0.25rem;
}

.carrousel-card_infos {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: var(--yb-spacing-xs);
  padding: 0.8rem 0.8rem 0.8rem 0;
  min-height: 0;
  overflow: hidden;
}

.card-link {
  color: inherit;
  font: inherit;
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  &:hover {
    text-decoration: underline;
  }
}

.rating {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--yb-color-black);
  margin-top: auto;

  p {
    font-weight: var(--yb-text-bold) !important;
  }

  .review-count {
    font-weight: var(--yb-text-light);
    font-size: var(--yb-text-sm);
  }

  .external-star {
    width: 18px;
    height: 18px;
    fill: #ff9800;
    flex-shrink: 0;
  }

  .provider-logo {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    object-fit: contain;
  }
}

.title_text_clamp {
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-card-title-color);
  line-height: var(--yb-text-lineheight);
  transition: color 0.3s ease-out;
}

.small_text {
  color: var(--yb-color-black);
  font-size: var(--yb-text-xs);
  line-height: var(--yb-text-lineheight);
}

.price_tag {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-color-black);
  margin-top: auto;
}

.rating + .price_tag {
  margin-top: 0;
}

.carrousel-card:hover .title_text_clamp {
  color: var(--yb-color-primary);
}

@media screen and (width < 480px) {
  .carrousel-card {
    grid-template-columns: 1fr 1.5fr;
  }
}

/* Mobile keeps the base design-token sizes; desktop scales text up for readability. */
@media screen and (width >= 1024px) {
  .title_text_clamp {
    font-size: calc(var(--yb-text-base) * 1.25);
  }

  .small_text {
    font-size: calc(var(--yb-text-xs) * 1.25);
  }

  .price_tag {
    font-size: calc(var(--yb-text-base) * 1.25);
  }

  .rating .review-count {
    font-size: calc(var(--yb-text-sm) * 1.2);
  }
}
</style>
